import { BACKGROUND_COLORS, TEXT_COLORS, normalizeColor } from "./formattingConfig.mjs";

const NODE_TYPES = new Set(["doc", "heading", "paragraph", "text", "hardBreak", "horizontalRule", "blockquote", "bulletList", "orderedList", "listItem", "taskList", "taskItem", "codeBlock", "imagePlaceholder"]);
const SUPPORTED_BLOCK_NODE_TYPES = new Set(["heading", "paragraph", "horizontalRule", "blockquote", "bulletList", "orderedList", "taskList", "codeBlock", "imagePlaceholder"]);
const MARK_TYPES = new Set(["bold", "italic", "underline", "strike", "highlight", "textStyle", "link", "code"]);
const EMPTY_TEXT_BLOCK_TYPES = new Set(["heading", "paragraph", "codeBlock"]);
const INLINE_MARKS = { strong: "bold", b: "bold", em: "italic", i: "italic", u: "underline", s: "strike", strike: "strike", del: "strike", mark: "highlight" };
const MAX_DOCUMENT_DEPTH = 100;
const MAX_DOCUMENT_NODES = 10000;
const ROOT_REQUIRED_FIELDS = ["type", "content"];
const ROOT_OPTIONAL_FIELDS = ["attrs"];
const NODE_REQUIRED_FIELDS = ["type"];
const NODE_OPTIONAL_FIELDS = ["content", "attrs", "marks", "text"];
const MARK_REQUIRED_FIELDS = ["type"];
const MARK_OPTIONAL_FIELDS = ["attrs"];

export function isTiptapDocument(value) {
  try {
    return isTiptapDocumentUnsafe(value);
  } catch {
    return false;
  }
}

function isTiptapDocumentUnsafe(value) {
  if (!isObject(value)
    || !schemaFieldsAreEnumerableData(value, ROOT_REQUIRED_FIELDS, ROOT_OPTIONAL_FIELDS)
    || value.type !== "doc" || !isDenseSchemaArray(value.content) || !validateFormattingAttrs(value.attrs)) return false;
  const pending = [];
  for (let index = 0; index < value.content.length; index += 1) {
    pending.push({ node: value.content[index], depth: 1, parentType: "doc" });
  }
  let nodeCount = 0;
  while (pending.length) {
    const { node, depth, parentType } = pending.pop();
    nodeCount += 1;
    if (nodeCount > MAX_DOCUMENT_NODES || depth > MAX_DOCUMENT_DEPTH || !validateNodeShape(node, parentType)) return false;
    if (Array.isArray(node.content)) {
      if (nodeCount + pending.length + node.content.length > MAX_DOCUMENT_NODES) return false;
      for (let index = 0; index < node.content.length; index += 1) {
        pending.push({ node: node.content[index], depth: depth + 1, parentType: node.type });
      }
    }
  }
  structuredClone(value);
  return true;
}

export function resolveArticleDocument(article = {}) {
  if (isTiptapDocument(article.contentJson)) {
    return { ...detachLegacyTitle(article.contentJson, article.title), source: "contentJson", warning: null };
  }
  return {
    document: articleToDocument(article),
    title: stringValue(article.title).trim(),
    source: "legacy",
    warning: article.contentJson == null ? null : "invalid-content-json"
  };
}

export function articleToDocument(article = {}) {
  const sections = Array.isArray(article.sections) && article.sections.length ? article.sections : [{ id: "section-1", heading: "", paragraphs: [""] }];
  const content = [
    paragraphNode(stringValue(article.summary), { role: "summary" })
  ];
  sections.forEach((section, index) => {
    const sectionId = stringValue(section?.id) || `section-${index + 1}`;
    content.push(headingNode(stringValue(section?.heading), { level: 2, sectionId }));
    const paragraphs = Array.isArray(section?.paragraphs) && section.paragraphs.length ? section.paragraphs : [""];
    content.push(...paragraphs.map(parseBlock));
  });
  return { type: "doc", content };
}

export function documentToArticle(document, baseArticle = {}) {
  const safeDocument = isTiptapDocument(document) ? document : articleToDocument(baseArticle);
  const hasArticleSemantics = safeDocument.content.some((node) =>
    node.attrs?.role === "title" || node.attrs?.role === "summary" || typeof node.attrs?.sectionId === "string"
  );
  if (!hasArticleSemantics) return markdownDocumentToArticle(safeDocument, baseArticle);
  const title = stringValue(baseArticle.title);
  let summary = "";
  const sections = [];
  let current = null;

  for (const node of safeDocument.content) {
    if (node.type === "heading" && node.attrs?.role === "title") {
      continue;
    } else if (node.type === "paragraph" && node.attrs?.role === "summary") {
      summary = inlineToHtml(node.content || []);
    } else if (node.type === "heading" && node.attrs?.sectionId) {
      current = { id: node.attrs.sectionId, heading: nodeText(node), paragraphs: [] };
      sections.push(current);
    } else if (current && ["paragraph", "blockquote", "bulletList", "orderedList", "taskList", "codeBlock", "horizontalRule", "imagePlaceholder"].includes(node.type)) {
      current.paragraphs.push(blockToHtml(node));
    }
  }

  if (!sections.length) sections.push({ id: "section-1", heading: "", paragraphs: [] });
  return {
    ...baseArticle,
    title,
    summary,
    sections,
    contentJson: clone(safeDocument)
  };
}

function markdownDocumentToArticle(document, baseArticle) {
  const content = document.content || [];
  const title = stringValue(baseArticle.title);
  let summary = "";
  const sections = [];
  let current = null;

  content.forEach((node) => {
    if (node.type === "heading") {
      current = { id: `section-${sections.length + 1}`, heading: nodeText(node), paragraphs: [] };
      sections.push(current);
      return;
    }
    if (node.type === "paragraph" && !summary && !current) {
      summary = inlineToHtml(node.content || []);
      return;
    }
    if (!["paragraph", "blockquote", "bulletList", "orderedList", "taskList", "codeBlock", "horizontalRule", "imagePlaceholder"].includes(node.type)) return;
    if (!current) {
      current = { id: `section-${sections.length + 1}`, heading: "", paragraphs: [] };
      sections.push(current);
    }
    current.paragraphs.push(blockToHtml(node));
  });

  if (!sections.length) sections.push({ id: "section-1", heading: "", paragraphs: [] });
  return { ...baseArticle, title, summary, sections, contentJson: clone(document) };
}

function detachLegacyTitle(document, articleTitle = "") {
  const cloned = clone(document);
  const index = cloned.content.findIndex((node) => node.type === "heading" && node.attrs?.role === "title");
  const legacyTitle = index >= 0 ? nodeText(cloned.content[index]) : "";
  if (index >= 0) cloned.content.splice(index, 1);
  return {
    document: cloned,
    title: stringValue(articleTitle).trim() || legacyTitle
  };
}

function parseBlock(value) {
  const raw = stringValue(value).trim();
  const block = raw.match(/^<(ul|ol|blockquote)\b[^>]*>([\s\S]*)<\/\1>$/i);
  if (!block) return { type: "paragraph", content: parseInline(raw) };
  const tag = block[1].toLowerCase();
  if (tag === "blockquote") return { type: "blockquote", content: [{ type: "paragraph", content: parseInline(stripParagraphTags(block[2])) }] };
  return parseList(tag, block[2]);
}

function parseList(tag, inner) {
  const content = [];
  for (const value of findTopLevelListItems(inner)) {
    const item = value.trim();
    content.push({ type: "listItem", content: parseListItemChildren(item) });
  }
  if (!content.length) content.push({ type: "listItem", content: [{ type: "paragraph", content: parseInline(inner.replace(/<[^>]+>/g, "")) }] });
  return { type: tag === "ul" ? "bulletList" : "orderedList", content };
}

function parseListItemChildren(value) {
  const children = [];
  const tags = /<\/?(p|ul|ol)\b[^>]*>/gi;
  const stack = [];
  let blockStart = -1;
  let contentStart = -1;
  let cursor = 0;
  let match;

  while ((match = tags.exec(value))) {
    const closing = /^<\s*\//.test(match[0]);
    const tag = match[1].toLowerCase();
    if (!closing) {
      if (!stack.length) {
        appendInlineParagraph(children, value.slice(cursor, match.index));
        blockStart = match.index;
        contentStart = tags.lastIndex;
      }
      stack.push(tag);
    } else if (stack.at(-1) === tag) {
      stack.pop();
      if (!stack.length) {
        if (tag === "p") children.push({ type: "paragraph", content: parseInline(value.slice(contentStart, match.index)) });
        else children.push(parseBlock(value.slice(blockStart, tags.lastIndex)));
        cursor = tags.lastIndex;
      }
    }
  }

  if (!stack.length) appendInlineParagraph(children, value.slice(cursor));
  if (!children.length) appendInlineParagraph(children, value);
  return children.length ? children : [{ type: "paragraph", content: [] }];
}

function appendInlineParagraph(children, value) {
  const text = value.trim();
  if (text) children.push({ type: "paragraph", content: parseInline(stripParagraphTags(text)) });
}

function findTopLevelListItems(value) {
  const items = [];
  const tags = /<\/?li\b[^>]*>/gi;
  let depth = 0;
  let contentStart = -1;
  let match;
  while ((match = tags.exec(value))) {
    const closing = /^<\s*\//.test(match[0]);
    if (!closing) {
      if (depth === 0) contentStart = tags.lastIndex;
      depth += 1;
    } else if (depth > 0) {
      depth -= 1;
      if (depth === 0) items.push(value.slice(contentStart, match.index));
    }
  }
  return items;
}

function parseInline(raw) {
  if (!/<\/?(?:strong|b|em|i|u|s|strike|del|mark|a|br)\b/i.test(raw)) {
    return raw ? [{ type: "text", text: raw }] : [];
  }
  const nodes = [];
  const marks = [];
  const tokens = raw.match(/<[^>]*>|[^<]+/g) || [];
  for (const token of tokens) {
    if (!token.startsWith("<")) {
      if (token) nodes.push(textNode(decodeEntities(token), marks));
      continue;
    }
    const tagMatch = token.match(/^<\s*(\/)?\s*([\w-]+)([^>]*)>/);
    if (!tagMatch) continue;
    const [, closing, rawTag, attrs = ""] = tagMatch;
    const tag = rawTag.toLowerCase();
    if (tag === "br" && !closing) {
      nodes.push({ type: "hardBreak" });
      continue;
    }
    let markType = INLINE_MARKS[tag];
    let markAttrs;
    if (tag === "a") {
      markType = "link";
      if (!closing) {
        const href = attrs.match(/\shref\s*=\s*(?:"([^"]*)"|'([^']*)'|([^\s>]+))/i);
        const url = href?.[1] || href?.[2] || href?.[3] || "";
        if (!isSafeUrl(url)) continue;
        markAttrs = { href: url };
      }
    }
    if (!markType) continue;
    if (closing) {
      const index = marks.map((mark) => mark.type).lastIndexOf(markType);
      if (index >= 0) marks.splice(index, 1);
    } else if (!marks.some((mark) => mark.type === markType)) {
      marks.push(markAttrs ? { type: markType, attrs: markAttrs } : { type: markType });
    }
  }
  return mergeTextNodes(nodes);
}

function blockToHtml(node) {
  if (node.type === "paragraph") return inlineToHtml(node.content || []);
  if (node.type === "codeBlock") return `<pre><code>${escapeHtml(nodeText(node))}</code></pre>`;
  if (node.type === "horizontalRule") return "<hr>";
  if (node.type === "imagePlaceholder") return formatImagePlaceholder(node.attrs);
  if (node.type === "blockquote") return `<blockquote>${(node.content || []).map(blockToHtml).join("<br><br>")}</blockquote>`;
  const tag = ["bulletList", "taskList"].includes(node.type) ? "ul" : "ol";
  return `<${tag}>${(node.content || []).map((item) => `<li>${listItemToHtml(item)}</li>`).join("")}</${tag}>`;
}

function listItemToHtml(item) {
  const children = item.content || [];
  const paragraphCount = children.filter((child) => child.type === "paragraph").length;
  return children.map((child) => child.type === "paragraph" && paragraphCount > 1 ? `<p>${blockToHtml(child)}</p>` : blockToHtml(child)).join("");
}

function inlineToHtml(nodes) {
  return nodes.map((node) => {
    if (node.type === "hardBreak") return "<br>";
    if (node.type !== "text") return nodeText(node);
    let value = escapeHtml(node.text);
    for (const mark of node.marks || []) {
      if (mark.type === "bold") value = `<strong>${value}</strong>`;
      else if (mark.type === "italic") value = `<em>${value}</em>`;
      else if (mark.type === "underline") value = `<u>${value}</u>`;
      else if (mark.type === "strike") value = `<s>${value}</s>`;
      else if (mark.type === "highlight") value = `<mark>${value}</mark>`;
      else if (mark.type === "link" && isSafeUrl(mark.attrs?.href)) value = `<a href="${escapeHtml(mark.attrs.href)}">${value}</a>`;
    }
    return value;
  }).join("");
}

function validateNodeShape(node, parentType) {
  if (!isObject(node)
    || !schemaFieldsAreEnumerableData(node, NODE_REQUIRED_FIELDS, NODE_OPTIONAL_FIELDS)
    || !NODE_TYPES.has(node.type)) return false;
  if (!validateFormattingAttrs(node.attrs)) return false;
  if (Array.isArray(node.content) && !isDenseSchemaArray(node.content)) return false;
  if (node.type === "text") return typeof node.text === "string" && node.content === undefined && validateMarks(node.marks);
  if (node.type === "hardBreak" || node.type === "horizontalRule") return node.content === undefined && node.text === undefined;
  if (node.type === "imagePlaceholder") {
    return node.content === undefined && isObject(node.attrs)
      && schemaFieldsAreEnumerableData(node.attrs, ["title", "placement", "description"], [])
      && ["title", "placement", "description"].every((name) => typeof node.attrs[name] === "string");
  }
  const content = node.content === undefined && EMPTY_TEXT_BLOCK_TYPES.has(node.type) ? [] : node.content;
  if (!Array.isArray(content)) return false;
  if (node.type === "doc") return false;
  if (node.type === "heading") {
    if (!isObject(node.attrs)
      || !schemaFieldsAreEnumerableData(node.attrs, ["level"], ["role", "sectionId"])
      || !Number.isInteger(node.attrs.level) || node.attrs.level < 1 || node.attrs.level > 5) return false;
    if (node.attrs.role !== undefined && node.attrs.role !== "title") return false;
    if (node.attrs.sectionId !== undefined && (typeof node.attrs.sectionId !== "string" || !node.attrs.sectionId)) return false;
    return childrenHaveTypes(content, ["text", "hardBreak"]);
  }
  if (node.type === "paragraph") {
    if (node.attrs !== undefined && !schemaFieldsAreEnumerableData(node.attrs, [], ["role"])) return false;
    if (node.attrs?.role !== undefined && node.attrs.role !== "summary") return false;
  }
  if (node.type === "codeBlock") return childrenHaveTypes(content, ["text"]);
  if (node.type === "listItem") return itemChildrenAreSupportedBlocks(content);
  if (["bulletList", "orderedList"].includes(node.type)) return childrenHaveTypes(content, ["listItem"]);
  if (node.type === "taskItem") {
    if (node.attrs !== undefined && !schemaFieldsAreEnumerableData(node.attrs, [], ["checked"])) return false;
    if (parentType !== "taskList") return false;
    if (node.attrs?.checked !== undefined && typeof node.attrs.checked !== "boolean") return false;
    return itemChildrenAreSupportedBlocks(content);
  }
  if (node.type === "taskList") return childrenHaveTypes(content, ["taskItem"]);
  if (node.type === "blockquote") return content.length > 0 && childrenAreSupportedBlocks(content);
  return childrenHaveTypes(content, ["text", "hardBreak"]);
}

function validateFormattingAttrs(attrs) {
  if (attrs === undefined) return true;
  if (!isObject(attrs)) return false;
  if (!schemaFieldsAreEnumerableData(attrs, [], ["textAlign", "indent", "role", "sectionId"])) return false;
  if (![undefined, null, "left", "center", "right"].includes(attrs.textAlign)) return false;
  return attrs.indent === undefined || (Number.isInteger(attrs.indent) && attrs.indent >= 0 && attrs.indent <= 4);
}

function childrenHaveTypes(children, types) {
  for (let index = 0; index < children.length; index += 1) {
    const child = children[index];
    if (!isObject(child) || !types.includes(child.type)) return false;
  }
  return true;
}

function childrenAreSupportedBlocks(children) {
  for (let index = 0; index < children.length; index += 1) {
    const child = children[index];
    if (!isObject(child) || !SUPPORTED_BLOCK_NODE_TYPES.has(child.type)) return false;
  }
  return true;
}

function itemChildrenAreSupportedBlocks(children) {
  return children[0]?.type === "paragraph" && childrenAreSupportedBlocks(children);
}

function validateMarks(marks) {
  if (marks === undefined) return true;
  if (!isDenseSchemaArray(marks)) return false;
  const seenTypes = new Set();
  for (let index = 0; index < marks.length; index += 1) {
    const mark = marks[index];
    if (!isObject(mark)
      || !schemaFieldsAreEnumerableData(mark, MARK_REQUIRED_FIELDS, MARK_OPTIONAL_FIELDS)
      || !MARK_TYPES.has(mark.type)) return false;
    if (seenTypes.has(mark.type)) return false;
    seenTypes.add(mark.type);
    if (mark.type === "link") {
      if (!isObject(mark.attrs)
        || !schemaFieldsAreEnumerableData(mark.attrs, ["href"], [])
        || !isSafeUrl(mark.attrs.href)) return false;
    } else if (mark.type === "textStyle" && !validateFormattingColor(mark.attrs, TEXT_COLORS)) {
      return false;
    } else if (mark.type === "highlight" && !validateFormattingColor(mark.attrs, BACKGROUND_COLORS)) {
      return false;
    }
  }
  return true;
}

function validateFormattingColor(attrs, palette) {
  if (attrs === undefined) return true;
  if (!isObject(attrs)) return false;
  try {
    const prototype = Object.getPrototypeOf(attrs);
    if (prototype !== Object.prototype && prototype !== null) return false;
    if (Reflect.ownKeys(attrs).some((name) => name !== "color")) return false;
    if (!schemaFieldsAreEnumerableData(attrs, [], ["color"])) return false;
    const color = attrs.color;
    return color === undefined || color === null
      || (typeof color === "string" && (color === "" || normalizeColor(color, palette) !== null));
  } catch {
    return false;
  }
}

function schemaFieldsAreEnumerableData(object, requiredFields, optionalFields) {
  return requiredFields.every((name) => hasEnumerableDataProperty(object, name, true))
    && optionalFields.every((name) => hasEnumerableDataProperty(object, name, false));
}

function hasEnumerableDataProperty(object, name, required) {
  const descriptor = Object.getOwnPropertyDescriptor(object, name);
  if (descriptor === undefined) return !required && !(name in object);
  return descriptor.enumerable === true && Object.prototype.hasOwnProperty.call(descriptor, "value");
}

function isDenseSchemaArray(value) {
  if (!Array.isArray(value)
    || value.length > MAX_DOCUMENT_NODES
    || Object.getPrototypeOf(value) !== Array.prototype) return false;
  const ownKeys = Reflect.ownKeys(value);
  if (ownKeys.length !== value.length + 1) return false;
  for (let index = 0; index < ownKeys.length; index += 1) {
    const key = ownKeys[index];
    if (key === "length") continue;
    if (typeof key !== "string") return false;
    const arrayIndex = Number(key);
    if (!Number.isInteger(arrayIndex)
      || arrayIndex < 0
      || arrayIndex >= value.length
      || String(arrayIndex) !== key) return false;
  }
  for (let index = 0; index < value.length; index += 1) {
    if (!hasEnumerableDataProperty(value, index, true)) return false;
  }
  return true;
}

function headingNode(text, attrs) { return { type: "heading", attrs, content: text ? [{ type: "text", text }] : [] }; }
function paragraphNode(text, attrs) { return { type: "paragraph", attrs, content: parseInline(text) }; }
function textNode(text, marks) { return marks.length ? { type: "text", text, marks: clone(marks) } : { type: "text", text }; }
function nodeText(node) { return node.type === "text" ? node.text : (node.content || []).map(nodeText).join(node.type === "hardBreak" ? "\n" : ""); }
function stripParagraphTags(value) { return value.replace(/^<p\b[^>]*>/i, "").replace(/<\/p>$/i, ""); }
function mergeTextNodes(nodes) {
  return nodes.filter((node) => node.type !== "text" || node.text).reduce((result, node) => {
    const previous = result.at(-1);
    if (previous?.type === "text" && node.type === "text" && JSON.stringify(previous.marks || []) === JSON.stringify(node.marks || [])) previous.text += node.text;
    else result.push(node);
    return result;
  }, []);
}
function decodeEntities(value) { return value.replaceAll("&lt;", "<").replaceAll("&gt;", ">").replaceAll("&quot;", '"').replaceAll("&#39;", "'").replaceAll("&amp;", "&"); }
function escapeHtml(value) { return stringValue(value).replaceAll("&", "&amp;").replaceAll("<", "&lt;").replaceAll(">", "&gt;").replaceAll('"', "&quot;"); }
function formatImagePlaceholder(attrs = {}) {
  return `![图片占位：${escapeMarkdownField(attrs.title)}]() ${escapeMarkdownField(attrs.placement)}：${escapeMarkdownField(attrs.description)}`;
}
function escapeMarkdownField(value) {
  return stringValue(value).replaceAll("\\", "\\\\").replaceAll("]", "\\]").replaceAll("：", "\\：").replaceAll("\r", "\\r").replaceAll("\n", "\\n");
}
function isSafeUrl(value) { return typeof value === "string" && /^https?:\/\//i.test(value); }
function isObject(value) { return value !== null && typeof value === "object" && !Array.isArray(value); }
function stringValue(value) { return typeof value === "string" ? value : ""; }
function clone(value) { return structuredClone(value); }
